import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export default function TrialBanner() {
  const { companyContext, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Don't show for super_admin or if no company context
  if (!companyContext?.company || user?.role === 'super_admin') {
    return null;
  }

  const company = companyContext.company;
  const subscription = company.subscription;

  // Only show for trial companies
  if (subscription?.tier !== 'trial' || !subscription?.trial_ends_at) {
    return null;
  }

  // Calculate days remaining
  const trialEndDate = new Date(subscription.trial_ends_at);
  const now = new Date();
  const daysRemaining = Math.ceil((trialEndDate - now) / (1000 * 60 * 60 * 24));

  // Don't show banner if more than 7 days remaining
  if (daysRemaining > 7) {
    return null;
  }

  // Determine urgency styling
  const isExpired = daysRemaining <= 0;
  const isCritical = daysRemaining <= 2;
  const isWarning = daysRemaining <= 7;

  const getBannerStyle = () => {
    if (isExpired) {
      return 'bg-red-600 border-red-700 text-white';
    }
    if (isCritical) {
      return 'bg-orange-500 border-orange-600 text-white';
    }
    return 'bg-yellow-500 border-yellow-600 text-yellow-950';
  };

  const getMessage = () => {
    if (isExpired) {
      return 'Your free trial has expired. Upgrade now to continue using all features.';
    }
    if (daysRemaining === 1) {
      return 'Your free trial expires tomorrow! Upgrade now to keep access to all features.';
    }
    return `Your free trial expires in ${daysRemaining} days. Upgrade now to continue using all features.`;
  };

  return (
    <div 
      className={`${getBannerStyle()} border-b px-4 py-3`}
      data-testid="trial-expiring-banner"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {isExpired || isCritical ? (
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <Clock className="h-5 w-5 flex-shrink-0" />
          )}
          <span className="font-medium text-sm sm:text-base">
            {getMessage()}
          </span>
        </div>
        <Button
          onClick={() => navigate('/subscription')}
          size="sm"
          className={`${
            isExpired || isCritical 
              ? 'bg-white text-red-600 hover:bg-gray-100' 
              : 'bg-yellow-950 text-yellow-50 hover:bg-yellow-900'
          } flex-shrink-0`}
          data-testid="upgrade-now-btn"
        >
          Upgrade Now
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
